import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, Loader, Receipt, Paperclip, X, Download } from 'lucide-react';
import { festivalExpensesAPI, handleApiError } from '../utils/api';
import { prepareImageForUpload } from '../utils/imageUpload';
import AttachmentViewer from './AttachmentViewer';
import { saveBlob } from '../utils/downloadFile';

export const EXPENSE_CATEGORIES = [
  { value: 'HOTEL', label: 'Hôtel' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'MEAL', label: 'Repas' },
  { value: 'OTHER', label: 'Autre' },
];

const categoryLabel = (value) => EXPENSE_CATEGORIES.find((c) => c.value === value)?.label || 'Autre';

const formatEuros = (value) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);

const formatDay = (str) =>
  str ? new Date(`${str}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

/**
 * Frais d'un festival (hôtel, transport, repas...), hors buy-in. Ils
 * s'ajoutent au poste "Frais" du bilan du festival et au bilan global.
 *
 * `festivals` : festivals proposés dans le formulaire ([{ id, title }], le
 * plus récent d'abord) ; `expenses` : tous mes frais de festival.
 */
const FestivalExpensesCard = ({ festivals, expenses, onCreated, onDeleted, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [viewing, setViewing] = useState(null); // justificatif ouvert dans la visionneuse
  const [exportingId, setExportingId] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0); // change pour vider le champ fichier
  const [newFiles, setNewFiles] = useState([]); // justificatifs à joindre au frais en cours de saisie
  const [form, setForm] = useState({ festivalId: '', category: 'HOTEL', label: '', amount: '', expenseDate: '' });

  const total = useMemo(() => expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0), [expenses]);

  // Frais regroupés par festival, le plus récent d'abord.
  const groups = useMemo(() => {
    const map = new Map();
    expenses.forEach((e) => {
      if (!map.has(e.festivalId)) {
        map.set(e.festivalId, { festivalId: e.festivalId, title: e.festivalTitle, total: 0, items: [] });
      }
      const g = map.get(e.festivalId);
      g.total += Number(e.amount) || 0;
      g.items.push(e);
    });
    return Array.from(map.values());
  }, [expenses]);

  const selectedFestivalId = form.festivalId || (festivals[0] ? String(festivals[0].id) : '');
  const canSubmit = selectedFestivalId && Number(form.amount) > 0 && !saving;

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const response = await festivalExpensesAPI.create({
        festivalId: Number(selectedFestivalId),
        category: form.category,
        label: form.label.trim() || null,
        amount: parseFloat(form.amount),
        expenseDate: form.expenseDate || null,
      });
      let created = response.data;
      // Justificatifs choisis dans le formulaire : envoyés un par un après la création.
      const failed = [];
      for (const file of newFiles) {
        try {
          const up = await festivalExpensesAPI.uploadAttachment(created.id, await prepareImageForUpload(file));
          created = { ...created, attachments: [up.data, ...(created.attachments || [])] };
        } catch (upErr) {
          failed.push(file.name);
        }
      }
      onCreated(created);
      setForm((prev) => ({ ...prev, label: '', amount: '' }));
      setNewFiles([]);
      setFileInputKey((k) => k + 1);
      if (failed.length > 0) {
        alert(`Le frais est enregistré, mais ces justificatifs n'ont pas pu être envoyés : ${failed.join(', ')}. Tu peux les rajouter depuis la ligne du frais.`);
      }
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible d'ajouter le frais : ${errTitle} - ${details}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm(`Supprimer ce frais (${formatEuros(expense.amount)}) ?`)) return;
    setDeletingId(expense.id);
    try {
      await festivalExpensesAPI.delete(expense.id);
      onDeleted(expense.id);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Impossible de supprimer le frais : ${errTitle} - ${details}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Archive ZIP de tous les justificatifs du festival (frais + tournois).
  const handleExport = async (festivalId) => {
    setExportingId(festivalId);
    try {
      const { blob, fileName } = await festivalExpensesAPI.exportReceipts(festivalId);
      saveBlob(blob, fileName);
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      alert(`Export impossible : ${errTitle} - ${details}`);
    } finally {
      setExportingId(null);
    }
  };

  const handleAddFiles = async (expense, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploadingId(expense.id);
    let current = expense;
    const failed = [];
    for (const file of files) {
      try {
        const up = await festivalExpensesAPI.uploadAttachment(expense.id, await prepareImageForUpload(file));
        current = { ...current, attachments: [up.data, ...(current.attachments || [])] };
      } catch (err) {
        const { details } = handleApiError(err);
        failed.push(`${file.name} (${details})`);
      }
    }
    if (current !== expense) onUpdated(current);
    setUploadingId(null);
    if (failed.length > 0) alert(`Envoi impossible : ${failed.join(', ')}`);
  };

  const handleDeleteAttachment = async (expense, attachment) => {
    if (!window.confirm(`Supprimer le justificatif « ${attachment.fileName} » ?`)) return;
    try {
      await festivalExpensesAPI.deleteAttachment(attachment.id);
      onUpdated({ ...expense, attachments: (expense.attachments || []).filter((a) => a.id !== attachment.id) });
    } catch (err) {
      alert('Impossible de supprimer le justificatif');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow mb-4 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-gray-50 transition"
      >
        <span className="flex items-center gap-2 font-semibold text-gray-800">
          <Receipt size={18} className="text-gray-500" />
          Frais de festival
          <span className="text-sm font-normal text-gray-500">(hôtel, transport, repas… hors buy-in)</span>
        </span>
        <span className="flex items-center gap-3">
          {expenses.length > 0 && (
            <span className="text-sm font-semibold text-gray-700">{formatEuros(total)}</span>
          )}
          {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-gray-100 p-4 space-y-5">
          {festivals.length === 0 ? (
            <p className="text-sm text-gray-500">
              Inscris-toi d'abord à un tournoi : tu pourras ensuite ajouter des frais à son festival.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-6">
                <label className="block text-xs text-gray-500 mb-1">Festival</label>
                <select
                  value={selectedFestivalId}
                  onChange={(e) => handleChange('festivalId', e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {festivals.map((f) => (
                    <option key={f.id} value={f.id}>{f.title}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Catégorie</label>
                <select
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {EXPENSE_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-4">
                <label className="block text-xs text-gray-500 mb-1">Libellé (facultatif)</label>
                <input
                  type="text"
                  maxLength={255}
                  value={form.label}
                  onChange={(e) => handleChange('label', e.target.value)}
                  placeholder="Ex : 3 nuits à l'hôtel"
                  className="w-full px-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Montant (€)</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  placeholder="Ex : 240"
                  className="w-full px-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Date (facultatif)</label>
                <input
                  type="date"
                  value={form.expenseDate}
                  onChange={(e) => handleChange('expenseDate', e.target.value)}
                  className="w-full px-2 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="sm:col-span-6">
                <label className="block text-xs text-gray-500 mb-1">Justificatifs (facultatif, photos ou PDF)</label>
                <input
                  key={fileInputKey}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={(e) => setNewFiles(Array.from(e.target.files || []))}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                />
                {newFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{newFiles.length} fichier{newFiles.length > 1 ? 's' : ''} sélectionné{newFiles.length > 1 ? 's' : ''}</p>
                )}
              </div>
              <div className="sm:col-span-2 flex items-end">
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {saving ? <Loader size={16} className="animate-spin" /> : <Plus size={16} />}
                  Ajouter
                </button>
              </div>
            </form>
          )}

          {groups.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun frais de festival enregistré.</p>
          ) : (
            <div className="space-y-4">
              {groups.map((g) => (
                <div key={g.festivalId} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between bg-gray-50 px-3 py-2">
                    <span className="font-medium text-gray-800">{g.title}</span>
                    <span className="flex items-center gap-3">
                      <button
                        onClick={() => handleExport(g.festivalId)}
                        disabled={exportingId === g.festivalId}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 disabled:opacity-50"
                        title="Télécharger tous les justificatifs de ce festival (ZIP)"
                      >
                        {exportingId === g.festivalId ? (
                          <Loader size={12} className="animate-spin" />
                        ) : (
                          <Download size={12} />
                        )}
                        Justificatifs (ZIP)
                      </button>
                      <span className="font-semibold text-gray-700">{formatEuros(g.total)}</span>
                    </span>
                  </div>
                  <ul className="divide-y divide-gray-100">
                    {g.items.map((it) => (
                      <li key={it.id} className="px-3 py-2 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 mr-2">
                              {categoryLabel(it.category)}
                            </span>
                            <span className="text-gray-700 break-words">{it.label || '—'}</span>
                            {it.expenseDate && (
                              <span className="text-gray-400 ml-2">{formatDay(it.expenseDate)}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="font-medium text-gray-900">{formatEuros(it.amount)}</span>
                            <button
                              onClick={() => handleDelete(it)}
                              disabled={deletingId === it.id}
                              className="text-red-600 hover:bg-red-50 p-1.5 rounded disabled:opacity-50"
                              title="Supprimer ce frais"
                              aria-label="Supprimer ce frais"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        {/* Justificatifs : liste + ajout (un ou plusieurs fichiers) */}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {(it.attachments || []).map((att) => (
                            <span
                              key={att.id}
                              className="inline-flex items-center gap-1 max-w-full bg-blue-50 text-blue-800 rounded-full pl-2 pr-1 py-0.5 text-xs"
                            >
                              <Paperclip size={12} className="shrink-0" />
                              <button
                                onClick={() => setViewing(att)}
                                className="truncate max-w-[10rem] hover:underline text-left"
                                title={att.fileName}
                              >
                                {att.fileName}
                              </button>
                              <button
                                onClick={() => handleDeleteAttachment(it, att)}
                                className="p-0.5 rounded-full hover:bg-blue-100 shrink-0"
                                title="Supprimer ce justificatif"
                                aria-label="Supprimer ce justificatif"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                          <label
                            className={`inline-flex items-center gap-1 text-xs text-blue-600 hover:underline cursor-pointer ${
                              uploadingId === it.id ? 'opacity-50 pointer-events-none' : ''
                            }`}
                          >
                            {uploadingId === it.id ? (
                              <Loader size={12} className="animate-spin" />
                            ) : (
                              <Plus size={12} />
                            )}
                            Justificatif
                            <input
                              type="file"
                              multiple
                              accept="image/jpeg,image/png,image/webp,application/pdf"
                              className="hidden"
                              onChange={(e) => {
                                handleAddFiles(it, e.target.files);
                                e.target.value = '';
                              }}
                            />
                          </label>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {viewing && (
        <AttachmentViewer
          attachment={viewing}
          loadUrl={festivalExpensesAPI.downloadAttachment}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

export default FestivalExpensesCard;
